// crewbit-admin/src/lib/industryToTradeKey.ts
//
// Tier 2-UI Phase A §WorldClass.4 — onboarding-as-defaults-source UI.
// Mirror of crewbit-mobile/lib/industryToTradeKey.ts. Maps existing onboarding
// `subIndustryId` → `trade_default_settings.trade_key` (Migration 272).
//
// 16 trade_keys live server-side; ~100 subIndustryId values live client-side.
// Most subIndustryIds map to 'other' since the CRA T2125 trade taxonomy is
// narrower than the industry-onboarding taxonomy. When the helper returns
// 'other', the wizard surfaces "Auto-derived: Other" + override prompt.

export type TradeKey =
  | 'carpentry'
  | 'cleaning'
  | 'electrical'
  | 'flooring'
  | 'general_contractor'
  | 'hvac'
  | 'landscaping'
  | 'lawn_care'
  | 'masonry'
  | 'mobile_detailing'
  | 'other'
  | 'painting'
  | 'pest_control'
  | 'plumbing'
  | 'roofing'
  | 'snow_removal';

const SUB_INDUSTRY_TO_TRADE_KEY: Record<string, TradeKey> = {
  // Home Services & Repairs
  electrical: 'electrical',
  plumbing: 'plumbing',
  hvac: 'hvac',
  handyman: 'general_contractor',
  painting: 'painting',
  carpentry: 'carpentry',
  pest_control: 'pest_control',
  pressure_washing: 'cleaning',

  // Cleaning
  residential_cleaning: 'cleaning',
  commercial_cleaning: 'cleaning',
  carpet_upholstery: 'cleaning',
  window_cleaning: 'cleaning',
  post_construction: 'cleaning',

  // Landscaping & Outdoor
  lawn_care: 'lawn_care',
  landscaping_design: 'landscaping',
  tree_service: 'landscaping',
  snow_removal: 'snow_removal',
  fence_service: 'landscaping',
  pool_service: 'landscaping',
  irrigation: 'landscaping',
  gutter_service: 'landscaping',

  // Automotive
  auto_detail_mobile: 'mobile_detailing',
  auto_detail_shop: 'mobile_detailing',
  car_wash_mobile: 'mobile_detailing',

  // Construction & Trades
  general_contractor: 'general_contractor',
  commercial_electrical: 'electrical',
  commercial_plumbing: 'plumbing',
  roofing: 'roofing',
  flooring: 'flooring',
  drywall: 'carpentry',
  masonry: 'masonry',
  demolition: 'general_contractor',
  framing: 'carpentry',
};

export function industryToTradeKey(subIndustryId: string | null | undefined): TradeKey {
  if (!subIndustryId) return 'other';
  return SUB_INDUSTRY_TO_TRADE_KEY[subIndustryId] ?? 'other';
}

export function isFallbackTradeKey(subIndustryId: string | null | undefined): boolean {
  if (!subIndustryId) return true;
  return !(subIndustryId in SUB_INDUSTRY_TO_TRADE_KEY);
}

export const PROVINCE_LABELS: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

export const TRADE_LABELS: Record<TradeKey, string> = {
  carpentry: 'Carpentry',
  cleaning: 'Cleaning',
  electrical: 'Electrical',
  flooring: 'Flooring',
  general_contractor: 'General Contractor',
  hvac: 'HVAC',
  landscaping: 'Landscaping',
  lawn_care: 'Lawn Care',
  masonry: 'Masonry',
  mobile_detailing: 'Mobile Detailing',
  other: 'Other',
  painting: 'Painting',
  pest_control: 'Pest Control',
  plumbing: 'Plumbing',
  roofing: 'Roofing',
  snow_removal: 'Snow Removal',
};
